/**
 * PHSIMS Web Admin - Auth Provider
 * Author: Iswe Solutions Team
 * 
 * React Admin authentication provider with dual support:
 * 1. Keycloak SSO (when enabled)
 * 2. Local JWT authentication (fallback)
 * 
 * All pages require authentication. Session validation happens on every route.
 */

import { AuthProvider } from 'react-admin';
import {
    fetchSSOConfig,
    keycloakLogin,
    keycloakLogout,
    getKeycloak,
    getAuthMethod,
    clearAuth,
} from './services/keycloakService';

const API_URL = import.meta.env.VITE_API_URL || '/api';
let sessionValidated = false;

interface LoginResponse {
    access_token: string;
    token_type: string;
    user: {
        id: number | string;
        email: string;
        username: string;
        full_name: string;
        role: string;
        is_active: boolean;
        is_superuser: boolean;
        must_reset_password?: boolean;
    };
    must_reset_password?: boolean;
    // Second-factor signalling
    mfa_required?: boolean;
    method?: 'totp' | 'email' | string;
    mfa_enrollment_required?: boolean;
}

// Thrown by login() when the password was accepted but a second factor is still
// needed. The login page catches this to collect the code (it is not a failure).
interface MfaChallengeError extends Error {
    mfaRequired: true;
    method: 'totp' | 'email';
}

interface UserIdentity {
    id: number | string;
    fullName: string;
    email: string;
    role: string;
    is_superuser?: boolean;
    permissions?: string[] | null;
    avatar?: string;
}

const authProvider: AuthProvider = {
    login: async ({ username, password, mfaCode }: { username: string; password: string; mfaCode?: string }) => {
        const ssoConfig = await fetchSSOConfig();
        
        // Check if we can use SSO (requires secure context for Web Crypto API)
        const canUseSSO = window.isSecureContext || 
                          window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          !!window.crypto?.subtle;
        
        if (ssoConfig.enabled && canUseSSO) {
            await keycloakLogin(window.location.origin);
            return Promise.resolve();
        }
        
        // Use URLSearchParams for OAuth2 form-urlencoded format
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);
        // Second factor (only present on the follow-up call after the challenge).
        if (mfaCode) {
            formData.append('mfa_code', mfaCode);
        }

        const request = new Request(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
        });

        try {
            const response = await fetch(request);
            
            if (!response.ok) {
                // Backend unreachable / proxy error — show maintenance message
                if (response.status === 502 || response.status === 503 || response.status === 504) {
                    throw new Error(
                        'PHSIMS is currently under maintenance. Please try again in a few minutes.'
                    );
                }

                // Try to parse JSON body — if it fails, the backend
                // returned non-JSON (e.g. nginx HTML error page)
                const errorData = await response.json().catch(() => null);

                if (!errorData) {
                    // Non-JSON response means the backend service is
                    // down or misconfigured, not a credential problem
                    throw new Error(
                        'PHSIMS is currently under maintenance. Please try again in a few minutes.'
                    );
                }
                
                // Handle account lockout (429 Too Many Requests)
                if (response.status === 429) {
                    const retryAfter = response.headers.get('Retry-After');
                    const minutes = retryAfter ? Math.ceil(parseInt(retryAfter) / 60) : 15;
                    throw new Error(
                        `Account temporarily locked due to too many failed login attempts. ` +
                        `Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}, ` +
                        `or contact an administrator to unlock your account.`
                    );
                }
                
                // Genuine auth error from the backend (wrong password, inactive user, etc.)
                throw new Error(errorData.detail || 'Invalid username or password');
            }

            const data: LoginResponse = await response.json();

            // Second factor required: password verified, but the backend is
            // waiting for a code. Signal the login page to collect it and call
            // login again with mfaCode. This is a prompt, not a failure — no
            // token or user is present yet, so we must not touch localStorage.
            if (data.mfa_required) {
                const challenge = new Error('Verification code required') as MfaChallengeError;
                challenge.mfaRequired = true;
                challenge.method = data.method === 'email' ? 'email' : 'totp';
                return Promise.reject(challenge);
            }

            // auth_token is now in an httpOnly cookie set by the backend — do NOT write to localStorage
            localStorage.setItem('user', JSON.stringify(data.user));
            localStorage.setItem('auth_method', 'local');

            // Check if password reset is required
            if (data.must_reset_password) {
                localStorage.setItem('must_reset_password', 'true');
                window.location.href = '/change-password';
                return Promise.resolve();
            }

            // MFA enforced for this role but not yet set up: funnel to enrolment
            // before work begins (the user is authenticated; this is a gate, not
            // a lockout — mirrors the must_reset_password funnel above).
            if (data.mfa_enrollment_required) {
                window.location.href = '/mfa-setup';
                return Promise.resolve();
            }

            const redirectPath = localStorage.getItem('post_login_redirect');
            if (redirectPath) {
                localStorage.removeItem('post_login_redirect');
                window.location.href = redirectPath;
            } else {
                window.location.href = '/';
            }
            return Promise.resolve();
        } catch (error) {
            // Network-level failures (DNS, connection refused, CORS, timeout)
            // mean the backend is unreachable — show maintenance message
            if (error instanceof TypeError && (error as TypeError).message === 'Failed to fetch') {
                return Promise.reject(
                    new Error('PHSIMS is currently under maintenance. Please try again in a few minutes.')
                );
            }
            return Promise.reject(error);
        }
    },

    logout: async () => {
        const authMethod = getAuthMethod();
        
        if (authMethod === 'keycloak') {
            await keycloakLogout(window.location.origin + '/login');
        } else {
            // Clear localStorage tokens and also delete the server-side httpOnly auth cookie
            clearAuth();
            fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
        }
        
        // Reset session validation flag on logout
        sessionValidated = false;
        
        return Promise.resolve();
    },

    checkError: async ({ status }: { status: number }) => {
        // 403 = authenticated but insufficient permissions — do NOT logout
        // Only logout on 401 (invalid/expired token)
        if (status === 403) {
            return Promise.resolve();
        }
        if (status === 401) {
            const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            if (path && path !== '/login') {
                localStorage.setItem('post_login_redirect', path);
            }
            const authMethod = getAuthMethod();
            
            if (authMethod === 'keycloak') {
                const keycloak = await getKeycloak();
                if (keycloak?.authenticated) {
                    try {
                        await keycloak.updateToken(30);
                        return Promise.resolve();
                    } catch {
                        await keycloakLogout();
                    }
                }
            }
            
            clearAuth();
            sessionValidated = false;
            return Promise.reject({ redirectTo: '/login', message: 'Session expired' });
        }
        return Promise.resolve();
    },

    checkAuth: async () => {
        const authMethod = getAuthMethod();
        
        if (authMethod === 'keycloak') {
            const keycloak = await getKeycloak();
            if (keycloak?.authenticated) {
                try {
                    await keycloak.updateToken(30);
                    if (keycloak.token) {
                        localStorage.setItem('auth_token', keycloak.token);
                    }
                    if (window.location.pathname === '/login') {
                        const redirectPath = localStorage.getItem('post_login_redirect');
                        if (redirectPath) {
                            localStorage.removeItem('post_login_redirect');
                            window.location.href = redirectPath;
                        }
                    }
                    return Promise.resolve();
                } catch {
                    clearAuth();
                    return Promise.reject({ redirectTo: '/login', message: 'Session expired' });
                }
            }
        }
        
        // For local auth: validate session once per page load via /me endpoint.
        // The httpOnly auth_token cookie is sent automatically by the browser.
        // We also check localStorage for backward compat (existing sessions until they expire).
        if (!sessionValidated) {
            try {
                const legacyToken = localStorage.getItem('auth_token');
                const response = await fetch(`${API_URL}/auth/me`, {
                    credentials: 'include',
                    headers: legacyToken ? { 'Authorization': `Bearer ${legacyToken}` } : {},
                });
                
                if (!response.ok) {
                    // CRITICAL: Only logout on verified auth errors (401/403)
                    // 502/503 = backend restarting during deploy, NOT auth error
                    if (response.status === 401 || response.status === 403) {
                        clearAuth();
                        const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                        if (path && path !== '/login') {
                            localStorage.setItem('post_login_redirect', path);
                        }
                        return Promise.reject({ redirectTo: '/login', message: 'Please login to continue' });
                    }
                    // For server errors (502, 503, 500), allow offline usage
                    console.warn('[AuthProvider] Backend returned', response.status, '- allowing offline usage');
                    sessionValidated = true;
                    return Promise.resolve();
                }
                
                // Update user info from backend
                const userData = await response.json();
                localStorage.setItem('user', JSON.stringify(userData));
                sessionValidated = true;
                if (window.location.pathname === '/login') {
                    const redirectPath = localStorage.getItem('post_login_redirect');
                    if (redirectPath) {
                        localStorage.removeItem('post_login_redirect');
                        window.location.href = redirectPath;
                    }
                }
            } catch (error) {
                console.error('Session validation failed:', error);
                // Allow offline usage if backend is unreachable
                sessionValidated = true;
            }
        }
        
        return Promise.resolve();
    },

    getPermissions: () => {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
            return Promise.reject();
        }
        const user = JSON.parse(userStr);
        return Promise.resolve(user.role);
    },

    getIdentity: (): Promise<UserIdentity> => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                return Promise.reject();
            }
            const user = JSON.parse(userStr);
            return Promise.resolve({
                id: user.id,
                fullName: user.full_name,
                email: user.email,
                role: user.role,
                is_superuser: user.is_superuser,
                permissions: user.permissions || null,
                avatar: undefined,
            });
        } catch (error) {
            return Promise.reject(error);
        }
    },
};

export default authProvider;

export const getAuthToken = (): string | null => {
    return localStorage.getItem('auth_token');
};

// Check if SSO is explicitly disabled via environment variable
const isSSODisabled = (): boolean => {
    const envDisabled = (import.meta as any).env?.VITE_SSO_DISABLED;
    return envDisabled === 'true' || envDisabled === '1';
};

export const initializeAuth = async (): Promise<void> => {
    // Skip SSO entirely if disabled via environment variable
    if (isSSODisabled()) {
        return;
    }
    
    // Check if we're in a secure context (HTTPS or localhost)
    // Web Crypto API required for PKCE is not available on plain HTTP
    const canUseSSO = window.isSecureContext || 
                      window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      !!window.crypto?.subtle;
    
    if (!canUseSSO) {
        return;
    }
    
    // Timeout for SSO config check - generous to avoid abort errors
    const timeoutPromise = new Promise<{ enabled: false }>((resolve) => 
        setTimeout(() => resolve({ enabled: false }), 10000)  // 10s timeout
    );
    
    try {
        const ssoConfig = await Promise.race([fetchSSOConfig(), timeoutPromise]);
        if (ssoConfig.enabled) {
            const { initKeycloak } = await import('./services/keycloakService');
            await initKeycloak();
        }
    } catch (error) {
        // Silent fail - SSO just won't be available
    }
};